import { GeofenceLocation, ProctoringOption } from "@verity/proctoring-option/types";
import { CategoryOptionsListProps, OptionProps } from "../proctoringOptionsGrid";
import styles from "./index.module.css";
import OptionBlock from "../optionBlock";
import i18nPackage from "../../../i18n/i18n";
import { useContext, useEffect, useState } from "react";
import { getUnsupportedToggleText, PROCTORING_OPTION_TEXT } from "../option-text";
import { CheckBoxMenuSubItem } from "../option-menus/checkBoxMenuSubItem";
import { CoreButton } from "../../../core";
import ExcludedWebTrafficRecordingUrlsList from "../option-modals/excludedWebTrafficRecordingUrlsList";
import { IncognitoWarningModal } from "../option-modals/incognito-warning-modal";
import KeyboardShortcutBlockListModal from "../option-modals/keyboardShortcutBlockList";
import GeofenceModal from "../option-modals/geofence-modal";
import { VerityApiContext } from "../../../api/fetch";

function OneScreenOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const { oneScreenEnforcementMode, hideOtherWindows } = option;
	const [bypassWasChecked, setBypassWasChecked] = useState<boolean>(oneScreenEnforcementMode !== "strict");

	const notAllowed = !isOptionAllowed("oneScreenEnforcementMode");
	return (
		<OptionBlock
			name="onlyOneScreen"
			checked={oneScreenEnforcementMode !== "off" && !disabled && !notAllowed}
			forceEnablingMessage={i18nPackage.t("editQuizPage.configurationStep.otherActivitiesTSX.onlyOneScreen")}
			updateState={(checked) =>
				updateOption({
					...option,
					oneScreenEnforcementMode: checked ? (bypassWasChecked ? "normal" : "strict") : "off",
				})
			}
			description={PROCTORING_OPTION_TEXT.oneScreenEnforcementMode.description}
			Icon={PROCTORING_OPTION_TEXT.oneScreenEnforcementMode.icon}
			contentHeader={PROCTORING_OPTION_TEXT.oneScreenEnforcementMode.header}
			disabled={notAllowed || hideOtherWindows}
			fullDisabled={disabled || notAllowed || hideOtherWindows}
			disabledMessage={
				notAllowed
					? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
					: i18nPackage.t("editQuizPage.configurationStep.otherActivitiesTSX.onlyOneScreen")
			}
			menuItems={
				isOptionAllowed("oneScreenEnforcementMode", "normal") &&
				isOptionAllowed("oneScreenEnforcementMode", "strict") ? (
					<CheckBoxMenuSubItem
						label={i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.bypassModeText")}
						checked={bypassWasChecked}
						onChange={(checked) => {
							updateOption({
								...option,
								oneScreenEnforcementMode: checked ? "normal" : "strict",
							});
							setBypassWasChecked(checked);
						}}
						partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("oneScreenEnforcementMode", "strict")}
					/>
				) : undefined
			}
			horizontalOrientation={horizontalOrientation}
			partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("oneScreenEnforcementMode")}
		/>
	);
}

function RecordWebTrafficOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const { recordWebTraffic, isExtension } = option;
	const [urlListModalShowing, setUrlListModalShowing] = useState(false);
	const [warningModalShowing, setWarningModalShowing] = useState(false);

	const notAllowed = !isOptionAllowed("recordWebTraffic");
	return (
		<>
			<OptionBlock
				name="recordWebTraffic"
				checked={Boolean(recordWebTraffic)}
				updateState={(checked) => {
					updateOption({
						...option,
						recordWebTraffic: checked,
					});
					if (checked && isExtension) {
						setWarningModalShowing(true);
					}
				}}
				description={PROCTORING_OPTION_TEXT.recordWebTraffic.description}
				Icon={PROCTORING_OPTION_TEXT.recordWebTraffic.icon}
				contentHeader={PROCTORING_OPTION_TEXT.recordWebTraffic.header}
				disabled={notAllowed}
				fullDisabled={disabled || notAllowed}
				disabledMessage={
					notAllowed
						? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
						: getUnsupportedToggleText(option, "recordWebTraffic", !recordWebTraffic, Boolean(recordWebTraffic))
				}
				menuItems={
					isOptionAllowed("excludedWebTrafficRecordingUrls") && (
						<CoreButton
							className={styles.openModalButton}
							aria-haspopup="dialog"
							onClick={() => setUrlListModalShowing(true)}
						>
							{i18nPackage.t("editQuizPage.configurationStep.otherActivitiesTSX.recordWebTraffic")}
						</CoreButton>
					)
				}
				horizontalOrientation={horizontalOrientation}
				partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("recordWebTraffic")}
			/>
			{urlListModalShowing && (
				<ExcludedWebTrafficRecordingUrlsList
					onClose={() => setUrlListModalShowing(false)}
					currOption={option}
					updateOption={updateOption}
				/>
			)}
			{warningModalShowing && (
				<IncognitoWarningModal
					description={i18nPackage.t("editQuizPage.configurationStep.incognitoModal.description.recordWebTraffic")}
					open={warningModalShowing}
					onClose={() => setWarningModalShowing(false)}
				/>
			)}
		</>
	);
}

function RecordInputOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const { recordInput } = option;
	const [modalShowing, setModalShowing] = useState(false);

	const notAllowed = !isOptionAllowed("recordInput");
	return (
		<>
			<OptionBlock
				name="recordInput"
				checked={Boolean(recordInput)}
				updateState={(checked) => {
					updateOption({
						...option,
						recordInput: checked,
					});
				}}
				description={PROCTORING_OPTION_TEXT.recordInput.description}
				Icon={PROCTORING_OPTION_TEXT.recordInput.icon}
				contentHeader={PROCTORING_OPTION_TEXT.recordInput.header}
				disabled={notAllowed}
				fullDisabled={disabled || notAllowed}
				disabledMessage={
					notAllowed
						? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
						: getUnsupportedToggleText(option, "recordInput", !recordInput, Boolean(recordInput))
				}
				menuItems={
					<CoreButton className={styles.openModalButton} aria-haspopup="dialog" onClick={() => setModalShowing(true)}>
						{i18nPackage.t("editQuizPage.configurationStep.otherActivitiesTSX.recordInput")}
					</CoreButton>
				}
				horizontalOrientation={horizontalOrientation}
				partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("recordInput")}
			/>
			{modalShowing && (
				<KeyboardShortcutBlockListModal
					onClose={() => setModalShowing(false)}
					currOption={option}
					updateOption={updateOption}
				/>
			)}
		</>
	);
}

function EnableGeofenceOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
	courseId,
}: OptionProps<T> & {
	courseId?: number;
}) {
	const { enableGeofence, geofenceLocations } = option;
	const { getCourseSettings } = useContext(VerityApiContext);
	const [modalShowing, setModalShowing] = useState(false);
	const [savedGeofences, setSavedGeofences] = useState<GeofenceLocation[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const notAllowed = !isOptionAllowed("enableGeofence");

	useEffect(() => {
		if (!courseId || !getCourseSettings) return;

		void (async () => {
			try {
				setIsLoading(true);
				const settings = await getCourseSettings(courseId);
				setSavedGeofences(settings.geofenceLocations);
			} catch (error) {
				setSavedGeofences([]);
			} finally {
				setIsLoading(false);
			}
		})();
	}, [courseId, getCourseSettings]);

	return (
		<>
			<OptionBlock
				name="enableGeofence"
				checked={Boolean(enableGeofence)}
				updateState={(checked) => {
					if (checked) setModalShowing(true);
					updateOption({
						...option,
						enableGeofence: checked,
						geofenceLocations: checked ? option.geofenceLocations : [],
					});
				}}
				description={PROCTORING_OPTION_TEXT.enableGeofence.description}
				Icon={PROCTORING_OPTION_TEXT.enableGeofence.icon}
				contentHeader={PROCTORING_OPTION_TEXT.enableGeofence.header}
				disabled={notAllowed}
				fullDisabled={disabled || notAllowed}
				disabledMessage={
					notAllowed
						? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
						: getUnsupportedToggleText(option, "enableGeofence", !enableGeofence, Boolean(enableGeofence))
				}
				menuItems={
					<CoreButton className={styles.openModalButton} aria-haspopup="dialog" onClick={() => setModalShowing(true)}>
						{i18nPackage.t("editQuizPage.configurationStep.otherActivitiesTSX.enableGeofence")}
					</CoreButton>
				}
				horizontalOrientation={horizontalOrientation}
				partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("enableGeofence")}
			/>
			{modalShowing && (
				<GeofenceModal
					selectedGeofence={geofenceLocations && geofenceLocations.length > 0 ? geofenceLocations[0] : undefined}
					presetGeofences={savedGeofences}
					onClose={() => {
						setModalShowing(false);
						// UI team wants to disable on close
						updateOption({
							...option,
							enableGeofence: false,
							geofenceLocations: [],
						});
					}}
					allowEdit={false}
					onSave={(selectedGeofence, geofences) => {
						updateOption({
							...option,
							// Perhaps in the future, we will support multiple geofences
							geofenceLocations: selectedGeofence ? [selectedGeofence] : [],
						});
						setSavedGeofences(geofences);
						setModalShowing(false);
					}}
					isLoading={isLoading}
				/>
			)}
		</>
	);
}

export default function OtherActivitiesOptions<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	shouldShowOption,
	isOptionAllowed,
	horizontalOrientation = false,
	getPartiallySupportedPlatforms,
	courseId,
}: CategoryOptionsListProps<T> & {
	courseId?: number;
}) {
	const { recordClipboard, uploadScratchNotes, restrictDevices, detectSidePanel } = option;

	const recordClipboardAllowed = isOptionAllowed("recordClipboard");
	const uploadScratchNotesAllowed = isOptionAllowed("uploadScratchNotes");
	const restrictDevicesAllowed = isOptionAllowed("restrictDevices");
	const detectSidePanelAllowed = isOptionAllowed("detectSidePanel");
	return (
		<div className={styles.proctorSettingsGrid}>
			{shouldShowOption("oneScreenEnforcementMode") && (
				<OneScreenOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}

			{shouldShowOption("recordWebTraffic") && (
				<RecordWebTrafficOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}

			{shouldShowOption("detectSidePanel") && (
				<OptionBlock
					name="detectSidePanel"
					checked={Boolean(detectSidePanel)}
					updateState={(checked) =>
						updateOption({
							...option,
							detectSidePanel: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.detectSidePanel.description}
					Icon={PROCTORING_OPTION_TEXT.detectSidePanel.icon}
					contentHeader={PROCTORING_OPTION_TEXT.detectSidePanel.header}
					disabled={!detectSidePanelAllowed}
					fullDisabled={disabled || !detectSidePanelAllowed}
					disabledMessage={
						!isOptionAllowed("detectSidePanel")
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(option, "detectSidePanel", !detectSidePanel, Boolean(detectSidePanel))
					}
					horizontalOrientation={horizontalOrientation}
				/>
			)}

			{shouldShowOption("enableGeofence") && (
				<EnableGeofenceOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
					courseId={courseId}
				/>
			)}

			{shouldShowOption("recordInput") && (
				<RecordInputOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}

			{shouldShowOption("recordClipboard") && (
				<OptionBlock
					name="recordClipboard"
					checked={Boolean(recordClipboard)}
					updateState={(checked) =>
						updateOption({
							...option,
							recordClipboard: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.recordClipboard.description}
					Icon={PROCTORING_OPTION_TEXT.recordClipboard.icon}
					contentHeader={PROCTORING_OPTION_TEXT.recordClipboard.header}
					disabled={!recordClipboardAllowed}
					fullDisabled={disabled || !recordClipboardAllowed}
					disabledMessage={
						!isOptionAllowed("recordClipboard")
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(option, "recordClipboard", !recordClipboard, Boolean(recordClipboard))
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("recordClipboard")}
				/>
			)}

			{shouldShowOption("uploadScratchNotes") && (
				<OptionBlock
					name="uploadScratchNotes"
					checked={Boolean(uploadScratchNotes)}
					updateState={(checked) =>
						updateOption({
							...option,
							uploadScratchNotes: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.uploadScratchNotes.description}
					Icon={PROCTORING_OPTION_TEXT.uploadScratchNotes.icon}
					contentHeader={PROCTORING_OPTION_TEXT.uploadScratchNotes.header}
					disabled={!uploadScratchNotesAllowed}
					fullDisabled={disabled || !uploadScratchNotesAllowed}
					disabledMessage={
						!isOptionAllowed("uploadScratchNotes")
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(option, "uploadScratchNotes", !uploadScratchNotes, Boolean(uploadScratchNotes))
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("uploadScratchNotes")}
				/>
			)}

			{shouldShowOption("restrictDevices") && (
				<OptionBlock
					name="restrictDevices"
					checked={Boolean(restrictDevices)}
					updateState={(checked) =>
						updateOption({
							...option,
							restrictDevices: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.restrictDevices.settingDescription}
					Icon={PROCTORING_OPTION_TEXT.restrictDevices.icon}
					contentHeader={PROCTORING_OPTION_TEXT.restrictDevices.header}
					disabled={!restrictDevicesAllowed}
					fullDisabled={disabled || !restrictDevicesAllowed}
					disabledMessage={
						!restrictDevicesAllowed
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(option, "restrictDevices", !restrictDevices, Boolean(restrictDevices))
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("restrictDevices")}
				/>
			)}
		</div>
	);
}
